import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { contactTypes } from 'src/constants/contact'
import { useAppDispatch, useAppSelector } from 'src/store'
import { submitContact } from 'src/useCase/contact'
import SimpleTextAreaField from 'src/components/form/SimpleTextAreaField'
import SimpleSelectField from 'src/components/form/SimpleSelectField'
import SimpleInputField from 'src/components/form/SimpleInputField'
import { EMAIL_REGEX } from 'src/utils/regex'
import { useLocation, useNavigate } from 'react-router-dom'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'

const initialForm = {
  email: '',
  name: '',
  type: '',
  message: '',
}

const ContactPage = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.ui.app.authInfo)

  const navigate = useNavigate()
  const location = useLocation()
  // [SCOPE] No.154 — pre-fill the email the patient already typed on the
  // duplicate-account flow (No_154_Client_QA_JP.md Q5) for guests without a
  // session; does not affect the logged-in prefill below.
  const contactState = location.state as
    | { email?: string; fromDuplicateAccount?: boolean }
    | undefined
  const prefilledEmail = contactState?.email
  // [SCOPE] Feedback (08/05) — a mid-registration session (e.g. phone
  // verification) already populates Redux authInfo, so `user?.id` alone
  // can't distinguish that from a real logged-in customer. Use the explicit
  // flag set by DuplicateAccountOptions.handleGoToContact instead.
  const fromDuplicateAccount = contactState?.fromDuplicateAccount

  const [formData, setFormData] = useState(initialForm)
  const [errors, setErrors] = useState(initialForm)

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        name: `${user.family_name} ${user.given_name}`,
        type: '',
        message: '',
      })
    } else if (prefilledEmail) {
      setFormData((prev) => ({ ...prev, email: prefilledEmail }))
    }
  }, [user, prefilledEmail])

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validateAll = () => {
    const newErrors = { ...initialForm }

    const emailRegex = /\S+@\S+\.\S+/

    if (!formData.email.trim()) newErrors.email = 'この項目は必須です'
    else if (!emailRegex.test(formData.email))
      newErrors.email = '正しいメールアドレスを入力してください'

    if (!formData.name.trim()) newErrors.name = 'この項目は必須です'
    if (!formData.type.trim()) newErrors.type = 'この項目は必須です'

    if (!formData.message.trim()) newErrors.message = 'この項目は必須です'
    else if (formData.message.length > 1000)
      newErrors.message = '1000文字以内で入力してください'

    setErrors(newErrors)
    return Object.values(newErrors).every((v) => v === '')
  }

  const validateField = (field: keyof typeof formData, value: string) => {
    let errorMsg = ''

    if (!value.trim()) errorMsg = 'この項目は必須です'
    else if (field === 'email' && !EMAIL_REGEX.test(value))
      errorMsg = '正しいメールアドレスを入力してください'

    setErrors((prev) => ({ ...prev, [field]: errorMsg }))
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!validateAll()) return

      const res = await dispatch(
        submitContact({
          params: {
            email: formData.email,
            name: formData.name,
            contact_type: formData.type,
            content: formData.message,
            id: '',
          },
        }),
      )

      if (res?.meta?.requestStatus === 'fulfilled') {
        setFormData(initialForm)
        setErrors(initialForm)
      }
    },
    [formData, dispatch],
  )

  const back = () => {
    // [SCOPE] No.154 (FB3) — guests (no session, e.g. arriving from the
    // duplicate-account flow) have no CONTACT_METHOD page to return to, so
    // send them back to the previous screen instead. Logged-in behavior
    // (navigate to CONTACT_METHOD) is unchanged.
    // [SCOPE] Feedback (08/05) — arriving via the duplicate-account flow
    // must always return there, even when authInfo is populated by a
    // mid-registration session (e.g. phone verification).
    if (!user?.id || fromDuplicateAccount) {
      return navigate(-1)
    }
    return navigate(PATH_MY_PAGE.CONTACT_METHOD)
  }

  return (
    <Wrapper>
      <div className="container">
        <h1>お問い合わせ</h1>

        <form onSubmit={handleSubmit}>
          <SimpleInputField
            label="メールアドレス"
            type="text"
            placeholder="入力してください"
            value={formData.email}
            onChange={(v) => handleChange('email', v)}
            onBlur={() => validateField('email', formData.email)}
            error={errors.email}
          />

          <SimpleInputField
            label="お名前"
            placeholder="入力してください"
            value={formData.name}
            onChange={(v) => handleChange('name', v)}
            onBlur={() => validateField('name', formData.name)}
            error={errors.name}
          />

          <SimpleSelectField
            label="問い合わせ種別"
            placeholder="選択してください"
            value={formData.type}
            onChange={(v) => handleChange('type', v)}
            onBlur={() => validateField('type', formData.type)}
            error={errors.type}
            options={contactTypes}
          />

          <SimpleTextAreaField
            label="お問い合わせ内容"
            placeholder="入力してください"
            value={formData.message}
            onChange={(v) => handleChange('message', v)}
            onBlur={() => validateField('message', formData.message)}
            error={errors.message}
            maxLength={1000}
          />

          <div className="button-wrap">
            <ContactBtn type="submit">送信する</ContactBtn>
            {/* [SCOPE] No.154 (FB3) — always show 戻る, including for guests
                without a session, so they can leave the contact form. */}
            <ContactBtn type="button" className="back" onClick={back}>
              戻る
            </ContactBtn>
          </div>
        </form>
      </div>
    </Wrapper>
  )
}

export default ContactPage

const Wrapper = styled.div`
  background-color: #f8f5eb;
  min-height: 100vh;
  padding: 60px 20px;
  display: flex;
  justify-content: center;

  .container {
    max-width: 500px;
    width: 100%;

    h1 {
      text-align: center;
      font-weight: 900;
      font-size: 28px;
      margin-bottom: 40px;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;

      label {
        font-weight: 800;
        font-size: 16px;
      }

      input,
      textarea,
      .ant-select-selector {
        border: 1px solid #c6b98c !important;
        border-radius: 10px !important;
        font-size: 16px;
        padding: 10px 14px;
        background-color: white;
      }

      input,
      textarea {
        width: 100%;
        resize: none;
        font-family: inherit;
      }
    }

    .ant-select-selector .ant-select-selection-placeholder,
    .ant-select-selector .ant-select-selection-item {
      line-height: 48px !important;
    }

    .button-wrap {
      text-align: center;
      margin-top: 20px;

      button {
        background-color: #c5b478;
        color: white;
        border: none;
        border-radius: 30px;
        padding: 12px 40px;
        font-size: 18px;
        font-weight: 700;
        cursor: pointer;
        transition: 0.2s;

        &:hover {
          opacity: 0.85;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .back {
        background-color: #090908;
        margin-top: 30px;
      }
    }
  }
`

const ContactBtn = styled.button`
  width: 100%;
  max-width: 300px;
`
